import { type MeasurementContext } from '@grafana/faro-core/dist/types/api/measurements/types';

import { getIsInitialRoute } from 'hooks/useTrackRouteVisit';
import { useMainPageStore } from 'modules/mainPage/stores/mainPageStore';

export enum ReaderType {
  bookReader = 'book_reader',
  classicReader = 'classic_reader'
}

export enum LoadingType {
  instantListening = 'instant_listening',
  openingFromLibrary = 'opening_from_library',
  loadingUrl = 'loading_url'
}

export enum VoiceType {
  freeVoice = 'free_voice',
  premiumVoice = 'premium_voice',
  unknown = 'unknown'
}

export const getLoadingType = () => {
  if (useMainPageStore.getState().instantListening) return LoadingType.instantListening;
  if (getIsInitialRoute()) return LoadingType.loadingUrl;
  return LoadingType.openingFromLibrary;
};

export enum MeasurementKey {
  libraryReady = 'library_ready',
  instantListeningBookReaderReady = 'instant_listening_book_reader_ready',
  instantListeningClassicReaderReady = 'instant_listening_classic_reader_ready',
  bookReaderReady = 'book_reader_ready',
  classicReaderReady = 'classic_reader_ready',
  sdkSetup = 'sdk_setup',
  sdkBundleFactoryCreation = 'sdk_bundle_factory_creation',
  sdkBundleCreation = 'sdk_bundle_creation',
  sdkGetAllVoices = 'sdk_get_all_voices',
  zustandIdbHydration = 'zustand_idb_hydration',
  zustandStoresHydration = 'zustand_stores_hydration',
  classicReaderTextLoading = 'classic_reader_text_loading',
  skipContentSettingsHydration = 'skip_content_settings_hydration',
  sdkIsItemAvailableOffline = 'sdk_is_item_available_offline',
  sdkStorageUtilizationCheck = 'sdk_storage_utilization_check',

  // Measurements introduced with Reader APIs
  // Initial load
  timeToFirstAudio = 'time_to_first_audio',
  timeToFirstPage = 'time_to_first_page',
  timeToFullBuffer = 'time_to_full_buffer',
  timeToIdle = 'time_to_idle',
  timeTo60Fps = 'time_to_60fps',
  timeToScrubberAvailable = 'time_to_scrubber_available',
  timeToHighlightAvailable = 'time_to_highlight_available',
  // Loading more content
  bookReaderTimeToLoadNewPage = 'book_reader_time_to_load_new_page',
  bookReaderTimeToLoadNewPageWithHighlight = 'book_reader_time_to_load_new_page_with_highlight',
  classicReaderTimeToLoadMoreBlocks = 'classic_reader_time_to_load_more_blocks',
  // Search API
  timeToSearch = 'time_to_search',
  // Miscellaneous
  droppedFramesPercentage = 'dropped_frames_percentage',

  // Various measurements for various work items that our web app is doing
  getAccountSettings = 'get_account_settings',
  importItem = 'import_item',
  importFile = 'import_file',
  createReadingBundle = 'create_reading_bundle',
  listeningExperienceRendered = 'listening_experience_rendered',
  initializeBookReader = 'initialize_book_reader',
  initializeListeningDependenciesForBookReader = 'initialize_listening_dependencies_for_book_reader',
  initializingSdk = 'initializing_sdk',
  getBundleCreationRequirements = 'get_bundle_creation_requirements',
  pspdfkitLoadLib = 'pspdfkit_load_lib',
  pspdfkitLoadInstance = 'pspdfkit_load_instance',
  getPageThumbnail = 'get_page_thumbnail',
  createContentBundlerOptions = 'create_content_bundler_options',
  createBundleFactory = 'create_bundle_factory',
  createBundles = 'create_bundles',
  readerBasedInit = 'reader_based_init',
  webPdfDocumentAdapterGetPages = 'web_pdf_document_adapter_get_pages',
  webPdfPageAdapterGetThumbnail = 'web_pdf_page_adapter_get_thumbnail',
  webPdfPageAdapterGetOutline = 'web_pdf_page_adapter_get_outline',
  webPdfPageAdapterGetTextBetween = 'web_pdf_page_adapter_get_text_between',
  webPdfPageAdapterGetImage = 'web_pdf_page_adapter_get_image',
  webPdfAdapterFactoryGetPdfDocumentAdapter = 'web_pdf_adapter_factory_get_pdf_document_adapter',
  webPdfAdapterFactoryLoadingPspdfKit = 'web_pdf_adapter_factory_loading_pspdfkit',
  setUser = 'set_user',
  getSubscriptionAndEntitlements = 'get_subscription_and_entitlements',
  getSettings = 'get_settings',
  sdkAccountFacadeGetAccountSettings = 'sdk_account_facade_get_account_settings'
}

type MeasurementContent<Context extends MeasurementContext> = {
  startTimestamp: number;
} & Context;

type MeasurementIdentifiers = Record<string, string>;
type MeasurementTypeSpec<MeasurementId extends MeasurementIdentifiers, Context extends MeasurementContext> = {
  measurementId: MeasurementId;
  content: MeasurementContent<Context>;
};

export type MeasurementTypes = {
  [MeasurementKey.libraryReady]: MeasurementTypeSpec<{}, {}>;
  [MeasurementKey.instantListeningBookReaderReady]: MeasurementTypeSpec<{}, {}>;
  [MeasurementKey.instantListeningClassicReaderReady]: MeasurementTypeSpec<{}, {}>;
  [MeasurementKey.bookReaderReady]: MeasurementTypeSpec<{}, {}>;
  [MeasurementKey.classicReaderReady]: MeasurementTypeSpec<{}, {}>;
  [MeasurementKey.sdkSetup]: MeasurementTypeSpec<{}, {}>;
  [MeasurementKey.sdkBundleFactoryCreation]: MeasurementTypeSpec<{}, {}>;
  [MeasurementKey.sdkBundleCreation]: MeasurementTypeSpec<{}, {}>;
  [MeasurementKey.sdkGetAllVoices]: MeasurementTypeSpec<{}, {}>;
  [MeasurementKey.zustandIdbHydration]: MeasurementTypeSpec<
    {},
    {
      storeName: string;
    }
  >;
  [MeasurementKey.zustandStoresHydration]: MeasurementTypeSpec<{}, { storeName: string }>;
  [MeasurementKey.classicReaderTextLoading]: MeasurementTypeSpec<{}, {}>;
  [MeasurementKey.skipContentSettingsHydration]: MeasurementTypeSpec<{}, {}>;
  [MeasurementKey.sdkIsItemAvailableOffline]: MeasurementTypeSpec<
    {
      itemId: string;
    },
    {}
  >;
  [MeasurementKey.sdkStorageUtilizationCheck]: MeasurementTypeSpec<{}, {}>;

  // Measurements introduced with Reader APIs
  [MeasurementKey.timeToFirstAudio]: MeasurementTypeSpec<{}, { readerType: ReaderType; loadingType: LoadingType; voiceType: VoiceType }>;
  [MeasurementKey.timeToFirstPage]: MeasurementTypeSpec<{}, { readerType: ReaderType; loadingType: LoadingType }>;
  [MeasurementKey.timeToFullBuffer]: MeasurementTypeSpec<{}, { readerType: ReaderType; loadingType: LoadingType }>;
  [MeasurementKey.timeToIdle]: MeasurementTypeSpec<{}, { readerType: ReaderType; loadingType: LoadingType }>;
  [MeasurementKey.timeTo60Fps]: MeasurementTypeSpec<{}, { readerType: ReaderType; loadingType: LoadingType }>;
  [MeasurementKey.timeToScrubberAvailable]: MeasurementTypeSpec<{}, { readerType: ReaderType; loadingType: LoadingType }>;
  [MeasurementKey.timeToHighlightAvailable]: MeasurementTypeSpec<{}, { readerType: ReaderType; loadingType: LoadingType }>;
  [MeasurementKey.bookReaderTimeToLoadNewPage]: MeasurementTypeSpec<{ pageIndex: string }, {}>;
  [MeasurementKey.bookReaderTimeToLoadNewPageWithHighlight]: MeasurementTypeSpec<{ pageIndex: string }, {}>;
  [MeasurementKey.classicReaderTimeToLoadMoreBlocks]: MeasurementTypeSpec<{}, {}>;
  [MeasurementKey.timeToSearch]: MeasurementTypeSpec<{}, { readerType: ReaderType }>;
  [MeasurementKey.droppedFramesPercentage]: MeasurementTypeSpec<{}, { readerType: ReaderType }>;
  [MeasurementKey.getAccountSettings]: MeasurementTypeSpec<{}, {}>;
  [MeasurementKey.importItem]: MeasurementTypeSpec<{}, {}>;
  [MeasurementKey.importFile]: MeasurementTypeSpec<{}, {}>;
  [MeasurementKey.createReadingBundle]: MeasurementTypeSpec<{}, {}>;
  [MeasurementKey.listeningExperienceRendered]: MeasurementTypeSpec<{}, {}>;
  [MeasurementKey.initializeBookReader]: MeasurementTypeSpec<{}, {}>;
  [MeasurementKey.initializeListeningDependenciesForBookReader]: MeasurementTypeSpec<{}, {}>;
  [MeasurementKey.initializingSdk]: MeasurementTypeSpec<{}, {}>;
  [MeasurementKey.getBundleCreationRequirements]: MeasurementTypeSpec<{}, {}>;
  [MeasurementKey.pspdfkitLoadLib]: MeasurementTypeSpec<{}, {}>;
  [MeasurementKey.pspdfkitLoadInstance]: MeasurementTypeSpec<{}, {}>;
  [MeasurementKey.getPageThumbnail]: MeasurementTypeSpec<{}, {}>;

  // Additional measurements for development profiling
  [MeasurementKey.createContentBundlerOptions]: MeasurementTypeSpec<{}, {}>;
  [MeasurementKey.createBundleFactory]: MeasurementTypeSpec<{}, {}>;
  [MeasurementKey.createBundles]: MeasurementTypeSpec<{}, {}>;
  [MeasurementKey.readerBasedInit]: MeasurementTypeSpec<{}, {}>;
  [MeasurementKey.webPdfDocumentAdapterGetPages]: MeasurementTypeSpec<{}, {}>;
  [MeasurementKey.webPdfPageAdapterGetThumbnail]: MeasurementTypeSpec<{}, {}>;
  [MeasurementKey.webPdfPageAdapterGetOutline]: MeasurementTypeSpec<{}, {}>;
  [MeasurementKey.webPdfPageAdapterGetTextBetween]: MeasurementTypeSpec<{}, {}>;
  [MeasurementKey.webPdfPageAdapterGetImage]: MeasurementTypeSpec<{}, {}>;
  [MeasurementKey.webPdfAdapterFactoryGetPdfDocumentAdapter]: MeasurementTypeSpec<{}, {}>;
  [MeasurementKey.webPdfAdapterFactoryLoadingPspdfKit]: MeasurementTypeSpec<{}, {}>;
  [MeasurementKey.setUser]: MeasurementTypeSpec<{}, {}>;
  [MeasurementKey.getSubscriptionAndEntitlements]: MeasurementTypeSpec<{}, {}>;
  [MeasurementKey.getSettings]: MeasurementTypeSpec<{}, {}>;
  [MeasurementKey.sdkAccountFacadeGetAccountSettings]: MeasurementTypeSpec<{}, {}>;
};

export type MeasurementType<Key extends MeasurementKey> = MeasurementTypes[Key];
export type MeasurementContextType<Key extends MeasurementKey> = Omit<MeasurementType<Key>['content'], 'startTimestamp'>;
export type MeasurementIdentifier<Key extends MeasurementKey> = MeasurementType<Key>['measurementId'];
